import { useState } from 'react'

import { Box, Text } from '@factorialco/deprecated-design-system'
import { F0Dialog } from '@factorialco/f0-react'
import {
  CreateItAssetDocumentManuallyDocument,
  ItAssetDocumentsDocument,
} from 'generated/resources/graphql'

import { useMutation } from 'modules/core/hooks/useMutation'
import { acceptedContentTypes } from 'modules/core/lib/fileFormats'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import DropzoneBox from 'modules/documents/components/Documents/DropzoneBox'
import FileList from 'modules/documents/components/FileList'

interface Props {
  assetId: string
  onClose: () => void
}

const UploadAssetDocumentModal = ({ assetId, onClose }: Props) => {
  const [files, setFiles] = useState<File[]>([])
  const [isUploading, setIsUploading] = useState(false)

  const [createItAssetDocument] = useMutation(CreateItAssetDocumentManuallyDocument, {
    extractorFn: (data) => data.itManagementMutations.createManuallyItmanagementItAssetDocument,
  })

  const handleFilesAdd = (newFiles: File[]) => {
    setFiles([...files, ...newFiles])
  }

  const handleFileDelete = (index: number) => {
    setFiles(files.filter((_, i) => i !== index))
  }

  const handleSubmit = async () => {
    if (files.length === 0) {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('it_management.asset_details.documents.no_files_selected'),
      })
      return
    }

    setIsUploading(true)

    try {
      for (const file of files) {
        const result = await createItAssetDocument({
          variables: {
            itAssetId: assetId,
            document: file,
          },
          refetchQueries: [
            {
              query: ItAssetDocumentsDocument,
              variables: { itAssetId: assetId },
            },
          ],
        })

        const handled = handleResult(result, () => undefined)
        if (!handled.success) {
          app.setFlash({
            type: 'wrong',
            message: i18n.t('common.documents_upload_failed'),
          })
          return
        }
      }

      app.setFlash({
        type: 'correct',
        message: i18n.t('common.saved_successfully'),
      })

      onClose()
    } catch (error) {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('common.documents_upload_failed'),
      })
    } finally {
      setIsUploading(false)
    }
  }

  return (
    <F0Dialog
      isOpen
      onClose={onClose}
      title={i18n.t('it_management.asset_details.documents.upload_document')}
      primaryAction={{
        label: isUploading ? i18n.t('common.saving') : i18n.t('common.save'),
        onClick: handleSubmit,
        loading: isUploading,
        disabled: files.length === 0 || isUploading,
      }}
      secondaryAction={{
        label: i18n.t('common.cancel'),
        onClick: onClose,
        disabled: isUploading,
      }}
    >
      <Box gap="s16" flexDirection="column">
        <Text size="200" weight="normal" color="grey700">
          {i18n.t('it_management.asset_details.documents.upload_document_helper_text')}
        </Text>
        <DropzoneBox
          onDrop={handleFilesAdd}
          message={i18n.t('documents.drop_here')}
          accept={acceptedContentTypes()}
          iconName="common-file-text-upload"
        />
        <FileList files={files} onDeleteFile={handleFileDelete} spaced={false} />
      </Box>
    </F0Dialog>
  )
}

export default UploadAssetDocumentModal
